import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity, ArrowRight, ClipboardList, Crown, Loader2,
  ShieldAlert, ShieldCheck, Star, Tent, Users,
} from "lucide-react";
import { useAuth } from "@/lib/auth";

// Admin dashboard — a real-time counter overview + jump-links to the
// verification queue and user management pages. Reads GET /admin/stats.
export default function AdminHome() {
  const { api } = useAuth();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/stats")
      .then(({ data }) => { if (!cancelled) setStats(data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Could not load admin stats."); });
    return () => { cancelled = true; };
  }, [api]);

  if (error) return <div className="card-elev p-6" style={{ color: "var(--premium)" }}>{error}</div>;
  if (!stats) return (
    <div className="max-w-3xl mx-auto py-12 flex justify-center">
      <Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--brand)" }} />
    </div>
  );

  const users = stats.users || {};
  const checkins = stats.checkins || {};
  const subs = stats.subscriptions || {};
  const topSites = stats.top_campsites || [];

  return (
    <div className="flex flex-col gap-6" data-testid="admin-home-page">
      <header>
        <div className="text-xs font-extrabold uppercase tracking-widest" style={{ color: "var(--on-surface-muted)" }}>
          Admin
        </div>
        <h1 className="text-2xl md:text-3xl font-extrabold mt-1" style={{ color: "var(--on-surface)" }}>
          Overview
        </h1>
      </header>

      {/* Users */}
      <Section title="Users" icon={Users}>
        <StatGrid tiles={[
          { label: "Total",   value: users.total,     hint: `${users.beta || 0} BETA` },
          { label: "Campers", value: users.campers,   hint: "role · camper" },
          { label: "Owners",  value: users.owners,    hint: "role · campsite_owner" },
          { label: "Admins",  value: users.admins,    hint: "role · admin" },
        ]} />
      </Section>

      {/* Check-ins */}
      <Section title="Check-ins" icon={Activity}>
        <StatGrid tiles={[
          { label: "Total",      value: checkins.total,     hint: "all-time" },
          { label: "Validated",  value: checkins.validated, hint: "stamps issued" },
          { label: "Pending",    value: checkins.pending,   hint: "awaiting owner" },
          { label: "Rejected",   value: checkins.rejected,  hint: "declined by owner" },
        ]} />
      </Section>

      {/* Subscriptions */}
      {(subs.total || (subs.camper && Object.keys(subs.camper).length) || (subs.owner && Object.keys(subs.owner).length)) && (
        <Section title="Active subscriptions" icon={Crown}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            <PlanBreakdown label="Camper" data={subs.camper || {}} />
            <PlanBreakdown label="Owner"  data={subs.owner  || {}} />
          </div>
        </Section>
      )}

      {/* Top campsites */}
      {topSites.length > 0 && (
        <Section title="Top campsites (by stamps)" icon={Star}>
          <div className="flex flex-col divide-y" style={{ borderColor: "var(--border-c)" }}>
            {topSites.slice(0, 5).map((c, i) => (
              <Link
                key={c.campsite_id}
                to={`/campsites/${c.campsite_id}`}
                className="flex items-center gap-3 py-3 hover:opacity-80"
                data-testid={`admin-top-${i}`}
                style={{ borderColor: "var(--border-c)" }}
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-extrabold"
                     style={{ background: "color-mix(in srgb, var(--brand) 12%, transparent)", color: "var(--brand)" }}>
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-extrabold truncate" style={{ color: "var(--on-surface)" }}>{c.name}</div>
                  <div className="text-[11px]" style={{ color: "var(--on-surface-muted)" }}>{c.state || "—"} · {c.verification_status || "unverified"}</div>
                </div>
                <div className="text-sm font-extrabold" style={{ color: "var(--brand)" }}>{c.stamps || c.checkin_count || 0} stamps</div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* Jump-links */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
        <Jump to="/admin/campsites" icon={ShieldCheck} title="Verification queue"
              desc="Review, approve, or reject campsite submissions" testId="admin-jump-campsites" />
        <Jump to="/admin/users" icon={ClipboardList} title="User management"
              desc="Search users, view roles, subscriptions" testId="admin-jump-users" />
        <Jump to="/admin/claims" icon={ShieldAlert} title="Campsite claims"
              desc="Approve/reject ownership handover requests" testId="admin-jump-claims" />
      </section>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <section>
      <h2 className="text-sm font-extrabold uppercase tracking-widest mb-3 inline-flex items-center gap-2"
          style={{ color: "var(--brand)" }}>
        <Icon className="w-4 h-4" /> {title}
      </h2>
      {children}
    </section>
  );
}

function StatGrid({ tiles }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
      {tiles.map((t) => (
        <div key={t.label} className="card-elev p-4" data-testid={`admin-stat-${t.label.toLowerCase()}`}>
          <div className="text-[10px] font-extrabold uppercase tracking-widest" style={{ color: "var(--on-surface-muted)" }}>
            {t.label}
          </div>
          <div className="text-2xl md:text-3xl font-extrabold" style={{ color: "var(--brand)" }}>
            {t.value != null ? Number(t.value).toLocaleString() : "—"}
          </div>
          {t.hint && <div className="text-[11px] mt-0.5" style={{ color: "var(--on-surface-muted)" }}>{t.hint}</div>}
        </div>
      ))}
    </div>
  );
}

function PlanBreakdown({ label, data }) {
  const entries = Object.entries(data);
  return (
    <div className="card-elev p-4">
      <div className="text-[10px] font-extrabold uppercase tracking-widest mb-2" style={{ color: "var(--on-surface-muted)" }}>
        {label}
      </div>
      {entries.length === 0 ? (
        <div className="text-sm" style={{ color: "var(--on-surface-muted)" }}>None active.</div>
      ) : (
        <div className="flex flex-col gap-1">
          {entries.map(([plan, count]) => (
            <div key={plan} className="flex items-center justify-between text-sm">
              <span style={{ color: "var(--on-surface)" }}>{plan}</span>
              <span className="font-extrabold" style={{ color: "var(--brand)" }}>{count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Jump({ to, icon: Icon, title, desc, testId }) {
  return (
    <Link to={to} className="card-elev p-5 flex items-center gap-4 hover:-translate-y-0.5 transition"
          data-testid={testId}>
      <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
           style={{ background: "color-mix(in srgb, var(--brand) 12%, transparent)", color: "var(--brand)" }}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-base font-extrabold" style={{ color: "var(--on-surface)" }}>{title}</div>
        <div className="text-xs" style={{ color: "var(--on-surface-muted)" }}>{desc}</div>
      </div>
      <ArrowRight className="w-4 h-4 opacity-60 shrink-0" />
    </Link>
  );
}
